import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { IWorkHours, OnboardingStepKey } from '@cbgms/api'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import OnboardingOpeningHoursForm from './OnboardingOpeningHoursForm'
import { useDispatch } from 'react-redux'
import { updateOpeningHours } from 'app/settings/state/OpeningHours/actions'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { OpeningHoursCTA } from './OpeningHoursCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: IWorkHours) => void
}

export const useOpeningHoursStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const [openingHours, setOpeningHours] = useState<IWorkHours | null>(null)
  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.OpeningHours
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)
  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const onStepSubmit = useCallback(
    (values: IWorkHours) => {
      setOpeningHours(values)
    },
    [setOpeningHours]
  )
  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    await dispatch(updateOpeningHours(openingHours))
  }, [openingHours, dispatch])

  return {
    Component: OpeningHoursStep,
    CTAComponent: OpeningHoursCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: { duplicationCheck: stepData.DuplicationCheck || [], basePath, onSubmit: complete.onSubmit },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('setting_opening_hour.opening_hours')}</>
}

export const OpeningHoursStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathOpeningHoursForm(basePath)} render={() => <OnboardingOpeningHoursForm {...rest} />} />
}
